import Controller from '@ember/controller';
import { action, setProperties } from '@ember/object';
import type Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { RecordData, Service } from 'webapp-types';
import { ActivityLogEvent } from 'webapp/enums/activity-log';
import { BillingFeature, ModelName, PopupEvent, SourceTypeCategory } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { getReturnUrlFromTransition } from 'webapp/utils/common';

import type { BoardShareRecord } from 'webapp/models';
import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import BoardState from 'webapp/models-custom/board-state';
import type DatawallGrid from 'webapp/models-custom/datawall-grid';
import type { GridNode } from 'webapp/models-custom/datawall-grid';

import type { NewBlockData } from 'webapp/services/model-manager/blocks';

export default class DesignerController extends Controller {
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('tour') declare tourService: Service.Tour;
	@service('store') declare storeService: Service.Store;
	@service('wizard') declare wizardService: Service.Wizard;
	@service('model-manager/block-previews')
	declare mmBlockPreviewsService: Service.ModelManager.BlockPreviews;

	@tracked board: Board | null = null;
	@tracked loop: BoardLoop | null = null;

	@tracked activeDatawallGrid: DatawallGrid | null = null;

	@tracked boardState: BoardState | null = null;

	enteringTransition: PublicTransition | null = null;

	@tracked _isLeftSidebarOpen = false;
	get isLeftSidebarOpen(): boolean {
		return this._isLeftSidebarOpen;
	}
	set isLeftSidebarOpen(value: boolean) {
		this._isLeftSidebarOpen = value;
		if (value && this.isRightSidebarOpen && !this.allowBothSidebarsOpen) {
			this.closeRightSidebar();
		}
	}

	get isRightSidebarOpen(): boolean {
		return !this.isBoardIndex;
	}

	constructor() {
		// eslint-disable-next-line prefer-rest-params
		super(...arguments);
		this.eventManagerService.on(AppEvent.BLOCK_SELECT, this, () => {
			next(this, this.checkToCloseLeftSidebar);
		});
	}

	get disableBoardsDropdown(): boolean {
		return this.mmBoardsService.savedRecords.length === 0;
	}

	get areBothSidebarsOpen(): boolean {
		return this.isLeftSidebarOpen && this.isRightSidebarOpen;
	}

	get isBoardIndex(): boolean {
		return (
			!this.boardState?.selectedBlock &&
			!this.navigationService.activatedRoute.includes('.block')
		); // bcos possibly the boardstate is not set yet (loading - on page refresh)
	}

	get preselectedSource() {
		return this.wizardService.preselectedSourceForDesigner;
	}

	updateBoardState(): void {
		const board = this.board;

		if (!board) {
			return;
		}

		const boardState = BoardState.createFromBoard(board);
		boardState.isViewOnly = false;

		boardState.onStateChange = (changesPayload) => {
			const requestService = this.requestService;

			let promiseChain: Promise<unknown> = Promise.resolve();

			// here in designer, we save all changes directly
			if (changesPayload.boardPreview?.changes) {
				if (changesPayload.boardPreview.changes.masterDateRange) {
					const payload = changesPayload.boardPreview.changes.masterDateRange,
						infolayer = this.infolayerService.loading('Saving date range...');

					promiseChain = requestService
						.api<RecordData.Payload>(
							'POST',
							`/boards/${board.id}/master-range`,
							payload,
						)
						.then(
							(reply) => {
								this.storeService.pushPayload(reply);
								infolayer.ok('Date range saved!');
							},
							(errorData) => {
								infolayer.error('There was an error saving the date range.');
								throw errorData;
							},
						)
						.catch(requestService.showAllValidationErrors.bind(requestService));
				}

				// reset not to trigger extra requests on next changes
				boardState._selectedMasterDateRange = null;

				const metricSetupChanges = changesPayload.boardPreview.changes.masterMetricSetup;
				if (metricSetupChanges) {
					board.blockObjs.forEach((block) => {
						if (block.id) {
							this.mmBlocksService.addBlockIdToRequestedSamples(block.id);
						}
					});

					void promiseChain.then(() => {
						const payload = metricSetupChanges;
						return requestService
							.api<RecordData.Payload>(
								'POST',
								`/boards/${board.id}/master-metric-setup`,
								payload,
							)
							.then((payload) => {
								this.storeService.pushPayload(payload);
							})
							.catch(
								this.requestService.showAllValidationErrors.bind(
									this.requestService,
								),
							);
					});
				}

				// reset to not show (1) in the filter
				boardState._selectedMasterDimensions = undefined;
				boardState._selectedMasterFilters = undefined;
			}

			const blockPreviews = changesPayload.blockPreviews || [];
			if (blockPreviews.length > 0) {
				void promiseChain.then(() => {
					this.mmBlocksService.updateBlocksOnBoardStateChange(blockPreviews);
				});
			}
		};
		this.boardState = boardState;
	}

	boardLoaded(): void {
		// event called from route when board is loaded so we can adjust sidebar state
		this.checkToCloseLeftSidebar();
	}

	get allowBothSidebarsOpen() {
		return !!this.board && !this.board.isWallPrimary;
	}

	checkToCloseLeftSidebar(): void {
		if (this.isRightSidebarOpen && !this.allowBothSidebarsOpen) {
			this.closeLeftSidebar();
		}
	}

	openLeftSidebar(): void {
		if (!this.isLeftSidebarOpen) {
			this.isLeftSidebarOpen = true;
		}
	}

	@action
	closeRightSidebar(): void {
		if (this.isRightSidebarOpen) {
			this.navigationService.transitionToRoute('designer.board', this.board?.model);
		}
	}

	closeLeftSidebar(): void {
		if (this.isLeftSidebarOpen) {
			this.isLeftSidebarOpen = false;
		}
	}

	updateBoard(board: Board): void {
		if (board !== this.board) {
			this.board = board;
			this.updateBoardState();

			// if left sidebar is already open, close right side panel
			if (this.isLeftSidebarOpen && !this.allowBothSidebarsOpen) {
				this.closeRightSidebar();
			}
		}
		this.updateLoopFromBoard(board);
	}

	updateLoop(loop: BoardLoop | null): void {
		if (loop !== this.loop) {
			this.loop = loop;
		}

		const board = this.board;
		if (loop && board) {
			// if current board is not from this loop, switch to first
			if (!loop.isBoardIncluded(board)) {
				const boardToOpen = loop.boardsEditableByUser[0];
				if (boardToOpen) {
					this.navigationService.transitionToRoute('designer.board', boardToOpen.id);
				} else {
					this.navigationService.transitionToRoute('designer.board');
				}
			}
		}
	}

	updateLoopFromBoard(board: Board | null): void {
		const loop = this.loop;
		if (board) {
			// if current board is not from this loop, switch to correct loop
			if (!loop?.isBoardIncluded(board)) {
				const loop = this.mmBoardLoopsService.findMostAppropriateLoopForBoard(board);
				this.updateLoop(loop);
			}
		} else {
			this.updateLoop(null);
		}
	}

	setNewBlockPositionAndSize(blockData: NewBlockData, callback?: () => void): void {
		const activeDatawallGrid = this.activeDatawallGrid;
		if (activeDatawallGrid) {
			const minSize = activeDatawallGrid.minimumSize;
			// find the biggest placeholder and slap it there
			let placeholder: GridNode | undefined;
			activeDatawallGrid.placeholdersGrid?.grid.nodes.forEach((node: GridNode) => {
				if (!placeholder) {
					placeholder = node;
				} else if (
					node.width >= minSize &&
					node.height >= minSize &&
					node.width * node.height > placeholder.width * placeholder.height
				) {
					placeholder = node;
				}
			});

			if (placeholder) {
				blockData.posX = placeholder.x;
				blockData.posY = placeholder.y;
				blockData.width = placeholder.width;
				blockData.height = placeholder.height;
			} else {
				// just put it somewhere with min size
				const found = activeDatawallGrid.findSpaceWithFallbackResize(0, 0, 2, 2),
					space = found.freeSpace;

				blockData.posX = space.x;
				blockData.posY = space.y;
				blockData.width = space.w;
				blockData.height = space.h;

				found.blocksResized.forEach((node) => {
					const resizedBlock = this.board?.blockObjs.find(
						(item) => item.localId === +node.id,
					);
					if (resizedBlock) {
						setProperties(resizedBlock, {
							posX: node.x,
							posY: node.y,
							width: node.width,
							height: node.height,
						});
						void resizedBlock.saveAndCatch();
					}
				});
			}

			if (callback) {
				callback();
			}
		}
	}

	@action
	saveBoard(): void {
		const board = this.board;

		if (!board) {
			return;
		}

		const isNew = board.isNew;
		board
			.save()
			.then((boardRecord) => {
				const sameBoard = boardRecord === board.model,
					boardId = board.id;

				// randomly, sometimes the resolved `board` does not have `id` (not saved yet)
				// skipping such case solves https://app.asana.com/0/8014827585025/1166160876800113
				if (!boardId) {
					return;
				}

				if (isNew) {
					// new board, mark it so we don't request 'data' again
					void this.mmBoardsService.markBoardRequestedAndSubscribeToDataChannel(boardId);
				}

				// designer can be closed in the middle of saving - we have to check that
				// and prevend undesired transition
				const routeName = this.navigationService.router.currentRouteName || '';
				if (isNew && sameBoard && routeName.includes('designer')) {
					// replace null with board id
					this.navigationService.replaceRoute('/layout-designer/' + boardId);
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	createNew(): void {
		if (this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
			this.navigationService.transitionToRoute('designer');
		}
	}

	@action
	copyBoard(): void {
		const board = this.board;
		if (board) {
			void this.mmBoardsService.duplicateBoard(board);
		}
	}

	@action
	deleteBoard(): void {
		const board = this.board;
		if (board) {
			const loop = this.loop;
			board.deleteObject('designer', () => {
				next(() => {
					if (loop) {
						const currentIndex = loop.boards.indexOf(board);
						const nextBoard =
							currentIndex === loop.boards.length - 1
								? loop.boards[currentIndex - 1]
								: loop.boards[currentIndex + 1];

						if (nextBoard) {
							this.navigationService.transitionToRoute(
								'designer.board',
								nextBoard.id,
							);
						} else {
							this.navigationService.transitionToRoute('designer.index');
						}
					} else {
						this.navigationService.transitionToRoute('designer.index');
					}
				});
				return 0;
			});
		}
	}

	@action
	openBoard(board: Board): void {
		if (this.board?.id !== board.id) {
			this.navigationService.transitionToRoute('designer.board', board.id);
		}
	}

	@action
	openLoop(loop: BoardLoop): void {
		if (loop.id) {
			this.mmActivityLogsService.createAndSaveActivityLogItem(
				ActivityLogEvent.BOARD_LOOP_VIEW,
				loop.id,
			);
		}
		this.updateLoop(loop);
	}

	@action
	toggleLeftSidebar(isOpen: boolean) {
		next(this, () => {
			if (isOpen) {
				this.openLeftSidebar();
			} else {
				this.closeLeftSidebar();
			}
		});
	}

	@action
	exitDesigner(toHome: boolean): void {
		let afterClose;

		if (toHome) {
			afterClose = () => {
				this.navigationService.transitionToRoute('index');
			};
		} else {
			if (this.onboardingService.triggerWizardDataboardIntroTourModalOnDesignerExit()) {
				return;
			}

			// trying to go back the way we came
			const enteringTransition = this.enteringTransition;
			let exitUrl = '';
			if (enteringTransition) {
				exitUrl = getReturnUrlFromTransition(
					enteringTransition,
					this.navigationService.router,
				);
			}

			const afterReturnTransition = enteringTransition?.data.afterReturn as
				((r: Route) => void) | null;

			afterClose = () => {
				this.navigationService.transitionToRoute(exitUrl || 'index').then((route) => {
					if (afterReturnTransition) {
						afterReturnTransition(route as Route);
					}
				});
			};
		}

		let isEligibleForAutoScheduleSnapshot = false,
			isForFree = false; // used for tracking event

		const board = this.board;

		if (board) {
			const accountBoardShares = this.storeService.peekAll(
					ModelName.BOARD_SHARE,
				) as BoardShareRecord[],
				currentUserCreatedBoardSharesCount = accountBoardShares.filter(
					(item) => item.createdById === this.currentUserService.numericId,
				).length;
			let isUserEligibleForAutoScheduleSnapshot = false;

			if (this.billingService.isPayingCustomer) {
				// Paying
				const accountSourcesCount = this.storeService.peekAll(ModelName.SOURCE).length;

				isUserEligibleForAutoScheduleSnapshot =
					!this.accountService.isClient &&
					currentUserCreatedBoardSharesCount === 0 &&
					accountSourcesCount >= 6 &&
					(!this.accountService.isAgency ||
						this.mmSpacesService.otherSpaceRecords.length >= 4);
			} else {
				// Free
				isForFree = true;

				const accountBoardCount = this.storeService.peekAll(ModelName.BOARD).length,
					sources = this.mmSourcesService.sourceModels,
					cloudSourcesCount = sources.filter(
						(source) => source.sourceType.category === SourceTypeCategory.CLOUD,
					).length;

				isUserEligibleForAutoScheduleSnapshot =
					!this.billingService.isPayingCustomer &&
					currentUserCreatedBoardSharesCount < 1 &&
					accountBoardCount >= 3 &&
					cloudSourcesCount >= 1;
			}

			if (
				isUserEligibleForAutoScheduleSnapshot &&
				!this.currentUserService.hasSeenBoardSchedulePopup
			) {
				const isBoardEligibleForAutoScheduleSnapshot =
					board.id && board.checkIsWallFilled() && board.checkIsAllMetricSetupsSpecific();

				if (isBoardEligibleForAutoScheduleSnapshot) {
					isEligibleForAutoScheduleSnapshot = true;
				}
			}
		}

		if (board?.id && isEligibleForAutoScheduleSnapshot) {
			const popup = this.mmBoardSharesService.openAutoScheduleSnapshot({
				boardId: board.id,
				isForFree,
			});
			popup.on(PopupEvent.CLOSING, this, afterClose);
		} else {
			afterClose();
		}
	}
}
